import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Code2, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { CodePanel } from '@/components/code-panel'
import { CostPanel } from '@/components/cost-panel'
import { InputPanel } from '@/components/input-panel'
import { OutputView } from '@/components/output-view'
import { StepTicker } from '@/components/step-ticker'
import { TourTabs } from '@/components/tour-rail'
import { Button } from '@/components/ui/button'
import { useDemoRun } from '@/hooks/use-demo-run'
import { getConfig } from '@/lib/api'
import { sampleUrl, STOPS, stopById, type StopId } from '@/lib/demos'
import { mountTurnstile } from '@/lib/turnstile'

import type { PlayConfig } from '@/shared/contract'

export const Route = createFileRoute('/')({
  validateSearch: (s: Record<string, unknown>): { demo?: StopId } =>
    typeof s.demo === 'string' ? { demo: s.demo as StopId } : {},
  component: Playground,
})

function Playground() {
  const { demo } = Route.useSearch()
  const navigate = useNavigate()
  const stop = stopById(demo)
  const go = (id: StopId) => void navigate({ to: '/', search: { demo: id } })
  const [config, setConfig] = useState<PlayConfig>()
  const inspectorRef = useRef<HTMLDialogElement>(null)
  const turnstileRef = useRef<HTMLDivElement>(null)
  const { input, params, setParams, state, runLive, chooseOwn, chooseSample } = useDemoRun(stop)

  useEffect(() => {
    getConfig()
      .then(setConfig)
      .catch(() => undefined)
    if (turnstileRef.current) mountTurnstile(turnstileRef.current)
  }, [])

  // ← / → move between stops unless the visitor is typing.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return
      const t = e.target as HTMLElement | null
      if (e.defaultPrevented || t?.closest('dialog[open]')) return
      if (
        t &&
        (t.tagName === 'INPUT' ||
          t.tagName === 'TEXTAREA' ||
          t.tagName === 'SELECT' ||
          t.isContentEditable)
      )
        return
      const i = STOPS.findIndex((s) => s.id === stop.id)
      const next = STOPS[i + (e.key === 'ArrowRight' ? 1 : -1)]
      if (next) go(next.id)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  })

  const origin =
    config?.publicOrigin ?? (typeof window !== 'undefined' ? window.location.origin : '')
  const isSample = input.source === 'sample'
  const busy = state.status === 'uploading' || state.status === 'running'
  const stale =
    state.status === 'done' &&
    JSON.stringify(params) !== JSON.stringify(state.params ?? stop.defaultParams)

  return (
    // Below xl the workspace stacks and the page scrolls (styles.css lifts overflow:hidden).
    <div className="flex min-h-dvh flex-col bg-background text-foreground xl:h-full xl:min-h-0">
      <TourTabs current={stop} onSelect={go} />

      <div className="grid min-h-0 flex-1 2xl:grid-cols-[minmax(0,1fr)_22rem]">
        <main className="grid min-h-0 xl:grid-cols-[30rem_minmax(0,1fr)]">
          <section
            aria-labelledby="playground-input"
            className="flex flex-col gap-3 border-b border-border p-4 lg:px-6 xl:min-h-0 xl:overflow-y-auto xl:border-r xl:border-b-0"
          >
            <div className="flex items-center justify-between gap-2">
              <h2
                id="playground-input"
                className="font-mono text-xs font-medium uppercase tracking-wider text-muted-foreground"
              >
                Input & options
              </h2>
              <Button
                variant="outline"
                size="sm"
                className="ml-auto 2xl:hidden"
                aria-controls="api-inspector"
                onClick={() => inspectorRef.current?.showModal()}
              >
                <Code2 data-icon="inline-start" />
                Code & cost
              </Button>
              <dialog
                ref={inspectorRef}
                id="api-inspector"
                aria-labelledby="api-inspector-title"
                aria-describedby="api-inspector-description"
                className="inset-y-0 right-0 left-auto m-0 ml-auto h-dvh w-[min(32rem,92vw)] max-w-none overflow-y-auto border-0 border-l border-border bg-background p-5 text-foreground shadow-2xl backdrop:bg-background/75 2xl:hidden"
              >
                <div className="flex flex-col gap-5">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h2 id="api-inspector-title" className="font-heading text-lg font-semibold">
                        Code & cost
                      </h2>
                      <p id="api-inspector-description" className="text-xs text-muted-foreground">
                        Copy the exact request, then check its estimated price.
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label="Close code and cost"
                      onClick={() => inspectorRef.current?.close()}
                    >
                      <X />
                    </Button>
                  </div>
                  <CodePanel
                    stop={stop}
                    params={params}
                    sampleUrl={
                      isSample && stop.sample ? sampleUrl(origin, stop.sample.file) : undefined
                    }
                    templateUrl={stop.template ? sampleUrl(origin, stop.template) : undefined}
                    fileName={
                      input.source === 'own' && input.file.size ? input.file.name : undefined
                    }
                  />
                  <CostPanel cost={state.result?.cost} sample={isSample} />
                </div>
              </dialog>
            </div>
            <InputPanel
              stop={stop}
              input={input}
              state={state}
              params={params}
              config={config}
              onParams={setParams}
              onSample={chooseSample}
              onOwn={chooseOwn}
              onRun={() => config && void runLive({ sitekey: config.turnstileSitekey })}
            />
            {busy ? (
              <StepTicker steps={stop.steps} step={state.step} startedAt={state.startedAt} />
            ) : null}
            {/* Managed Turnstile may need a click: keep the widget next to the Run button. */}
            <div ref={turnstileRef} className="empty:hidden" />
          </section>

          <section
            aria-label="Result"
            className="flex min-h-[70vh] flex-col lg:h-[70vh] lg:min-h-0 xl:h-auto xl:min-h-0"
          >
            <div className="flex h-9 flex-none items-center justify-between border-b border-border px-4 lg:px-6">
              <h2 className="font-mono text-xs font-medium uppercase tracking-wider">Output</h2>
              <span className="text-xs text-muted-foreground">
                {busy
                  ? 'Processing'
                  : stale
                    ? 'Edited · run again'
                    : state.status === 'done'
                      ? 'Ready'
                      : state.status === 'error'
                        ? 'Error'
                        : 'Waiting for input'}
              </span>
            </div>
            <div className="min-h-0 flex-1">
              {state.status === 'done' ? (
                <OutputView stop={stop} state={state} input={input} origin={origin} />
              ) : (
                <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                  {busy
                    ? 'Working…'
                    : state.status === 'error'
                      ? state.error
                      : stop.kind === 'none'
                        ? 'Press Run to generate the PDF.'
                        : 'Pick a sample or drop a file.'}
                </div>
              )}
            </div>
          </section>
        </main>

        <aside className="hidden min-h-0 flex-col gap-4 border-l border-border p-4 2xl:flex 2xl:overflow-y-auto">
          <CodePanel
            stop={stop}
            params={params}
            sampleUrl={isSample && stop.sample ? sampleUrl(origin, stop.sample.file) : undefined}
            templateUrl={stop.template ? sampleUrl(origin, stop.template) : undefined}
            fileName={input.source === 'own' && input.file.size ? input.file.name : undefined}
          />
          <CostPanel cost={state.result?.cost} sample={isSample} />
        </aside>
      </div>
    </div>
  )
}
